import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { subcampaignAwarenessText } from '../utils/index';

const useStyles = makeStyles(() => ({
  boosted: {
    marginTop: '8px',
    marginBottom: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'linear-gradient(90deg, #7B2FF7 0%, #F107A3 100%)',
    color: '#FFF',
    fontSize: '0.78rem',
    fontWeight: 600,
    lineHeight: 1.35,
  },
  boostedHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    fontWeight: 800,
    marginBottom: '4px',
  },
  // Turbo (link exclusivo): visual próprio para o cliente perceber que aquilo
  // não é o boost que todo mundo está vendo.
  turbo: {
    background: 'linear-gradient(90deg, #FF8A00 0%, #E52E71 100%)',
  },
  countdown: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 700,
    background: 'rgba(0, 0, 0, 0.25)',
    borderRadius: '6px',
    padding: '1px 8px',
  },
}));

function remainingMs(endsAt) {
  return new Date(endsAt).getTime() - Date.now();
}

function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

// Aviso de transparência de uma subcampanha de incentivo. Com boost ativo
// (o backend só serializa active_boost dentro da janela), o próprio aviso vira
// o banner do boost — assim fica claro a qual campanha de incentivo o boost
// pertence quando há mais de uma. Ao zerar o countdown volta ao aviso normal
// e dispara onBoostExpire (refetch) quando fornecido.
//
// Os dois textos do banner (chamada do topo e corpo) vêm do próprio período
// quando cadastrados no backoffice — active_boost.title e .message. Sem eles,
// cai no "⚡ BOOST ATIVO" e na frase montada com o ratio boostado. Com texto
// próprio o emoji padrão sai: quem escreve escolhe o seu. Cada campo tem
// fallback independente — dá pra customizar só um dos dois.
//
// Período turbo (active_boost.multiplier, liberado por link exclusivo — o
// backend só o serializa para quem tem o hash na session) usa gradiente e
// chamada próprios: é uma oferta que os outros visitantes não estão vendo.
export default function SubcampaignHint({ sub, className, onBoostExpire }) {
  const classes = useStyles();
  const boost = sub.active_boost;
  const [remaining, setRemaining] = React.useState(() => (boost ? remainingMs(boost.ends_at) : 0));
  const expiredRef = React.useRef(false);

  React.useEffect(() => {
    if (!boost) return undefined;
    expiredRef.current = false;
    setRemaining(remainingMs(boost.ends_at));
    const interval = setInterval(() => {
      const ms = remainingMs(boost.ends_at);
      setRemaining(ms);
      if (ms <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        clearInterval(interval);
        if (onBoostExpire) onBoostExpire();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [boost && boost.ends_at]);

  if (!boost || remaining <= 0) {
    return <div className={className}>🎁 {subcampaignAwarenessText(sub)}</div>;
  }

  const turbo = boost.multiplier > 1;
  const defaultTitle = turbo ? `🚀 TURBO x${boost.multiplier}` : '⚡ BOOST ATIVO';

  return (
    <div className={`${classes.boosted}${turbo ? ` ${classes.turbo}` : ''}`}>
      <div className={classes.boostedHeader}>
        <span>{boost.title ? boost.title : defaultTitle}</span>
        <span className={classes.countdown}>Termina em {formatRemaining(remaining)}</span>
      </div>
      {boost.message ? boost.message : <>🎁 {subcampaignAwarenessText(sub, boost)}</>}
    </div>
  );
}
